import React, { useState } from "react";
import * as Icons from "lucide-react";
import { api, apiError } from "@/lib/api";
import { RagBadge } from "@/components/common/RagBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { WorkGroup } from "@/components/workspace/WorkItemRow";
import { WorkItemDialog } from "@/components/workspace/WorkItemDialog";
import { LeaveDecisionRow, CorrectionDecisionRow } from "@/components/people/DecisionRows";
import { PERIOD_STATE } from "@/lib/workspace";
import { fmtValue } from "@/lib/format";
import { toast } from "sonner";

function VerifyRow({ r, onChanged }) {
  const [querying, setQuerying] = useState(false);
  const [note, setNote] = useState("");
  const st = PERIOD_STATE[r.state] || PERIOD_STATE.unknown;
  const decide = async (decision) => {
    try {
      await api.post(`/workspace/kpis/${r.kpi_id}/verify`, { period_key: r.period_key, decision, note: decision === "query" ? note : null });
      toast.success(decision === "verify" ? "Result verified" : "Result queried — the person who entered it has been told");
      onChanged();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <div className="amden-card p-4" data-testid={`verify-row-${r.kpi_id}-${r.period_key}`}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-[#0B1729]">{r.kpi_name}</div>
          <div className="text-xs text-[#1E2D44]/55">{r.label} · entered by {r.entered_by_name}</div>
        </div>
        <div className="font-display text-lg font-bold text-[#0B1729]">{fmtValue(r.value, r.unit)}</div>
        <RagBadge rag={st.rag}>{st.label}</RagBadge>
        <div className="flex gap-2">
          <Button size="sm" className="bg-[#3D8B65] text-white hover:bg-[#2E6B4E]" onClick={() => decide("verify")} data-testid={`approve-kpi-${r.kpi_id}-${r.period_key}`}>Verify</Button>
          <Button size="sm" variant="outline" onClick={() => setQuerying(!querying)} data-testid={`query-kpi-${r.kpi_id}-${r.period_key}`}>Query</Button>
        </div>
      </div>
      {querying && (
        <div className="mt-3 flex gap-2">
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="What needs checking?" data-testid="approval-query-note" />
          <Button disabled={!note.trim()} onClick={() => decide("query")} data-testid="approval-query-send">Send</Button>
        </div>
      )}
    </div>
  );
}

export function ApprovalsTab({ data, onChanged }) {
  const [open, setOpen] = useState(null);
  const kpis = data.kpi_verifications;
  return (
    <div data-testid="approvals-tab">
      <p className="mb-5 flex items-center gap-1.5 text-sm text-[#1E2D44]/60">
        <Icons.ShieldCheck size={15} className="text-[#B87F1E]" /> Nobody can approve their own work, leave or time, or verify a result they entered.
      </p>
      <WorkGroup title="Work waiting for your review" items={data.reviews} onOpen={setOpen} showOwner testId="approvals-reviews" empty="Nothing is waiting for your review." />
      <section data-testid="approvals-kpis">
        <div className="mb-2.5 flex items-center gap-2">
          <h3 className="font-display text-sm font-semibold text-[#0B1729]">KPI results to verify</h3>
          <span className="rounded-full bg-[#E4E8EF] px-2 py-0.5 text-[11px] font-medium text-[#1E2D44]/60">{kpis.length}</span>
        </div>
        {kpis.length === 0
          ? <p className="rounded-xl border border-dashed border-[#D2D9E3] px-4 py-5 text-center text-sm text-[#1E2D44]/45">No KPI results are waiting for a check.</p>
          : <div className="space-y-2">{kpis.map((r) => <VerifyRow key={`${r.kpi_id}-${r.period_key}`} r={r} onChanged={onChanged} />)}</div>}
      </section>
      {data.leave_requests?.length > 0 && (
        <section className="mt-7" data-testid="approvals-leave">
          <Heading title="Time off to approve" n={data.leave_requests.length} />
          <div className="space-y-2">{data.leave_requests.map((r) => <LeaveDecisionRow key={r.id} r={r} onChanged={onChanged} />)}</div>
        </section>
      )}
      {data.time_corrections?.length > 0 && (
        <section className="mt-7" data-testid="approvals-time">
          <Heading title="Time corrections to approve" n={data.time_corrections.length} />
          <div className="space-y-2">{data.time_corrections.map((c) => <CorrectionDecisionRow key={c.id} c={c} tz={data.timezone} onChanged={onChanged} />)}</div>
        </section>
      )}
      {open && <WorkItemDialog actionId={open.id} onClose={() => setOpen(null)} onChanged={onChanged} />}
    </div>
  );
}

const Heading = ({ title, n }) => (
  <div className="mb-2.5 flex items-center gap-2">
    <h3 className="font-display text-sm font-semibold text-[#0B1729]">{title}</h3>
    <span className="rounded-full bg-[#E4E8EF] px-2 py-0.5 text-[11px] font-medium text-[#1E2D44]/60">{n}</span>
  </div>
);
